import { useState } from 'react';

import { btn, field } from '@/components/ui/styles';
import http from '@/utils/http';

type Links = { url: string; group_url: string; linked: boolean };

/**
 * The lead's personal bot links (Q11, TG-1). Sent to the owner, the bot link ties their
 * Telegram account to this lead, and the groups they are in link to it without asking
 * for an email. "Add the bot to their group" opens Telegram to pick a group: the bot
 * joins it, and the group is this lead's at once.
 */
export const TelegramLinkButton = ({ leadId, linked }: { leadId: string; linked: boolean }) => {
  const [state, setState] = useState<'idle' | 'copied' | 'shown' | 'opened' | 'off' | 'failed'>('idle');
  const [url, setUrl] = useState('');
  const links = () => http.post<Links>(`/leads/${encodeURIComponent(leadId)}/telegram-link`);
  const failed = (err: unknown) => setState((err as { response?: { status?: number } }).response?.status === 404 ? 'off' : 'failed');
  const copy = async () => {
    let link: string;
    try {
      link = (await links()).data.url;
    } catch (err) {
      failed(err);
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      setState('copied');
    } catch {
      // Safari and iOS allow copying only during the tap itself, which the request outlasts: show the link instead.
      setUrl(link);
      setState('shown');
    }
  };
  // The tab opens on the click itself, so no pop-up blocker stops it; the link fills it in.
  const addToGroup = async () => {
    const tab = window.open('', '_blank');
    try {
      const r = await links();
      if (tab) tab.location.href = r.data.group_url;
      else window.location.href = r.data.group_url;
      setState('opened');
    } catch (err) {
      tab?.close();
      failed(err);
    }
  };
  const note = {
    idle: linked ? 'Their Telegram account is linked through the bot link.' : 'The bot link ties their Telegram account, and their groups, to this lead.',
    copied: 'Copied. Send it to the owner.',
    shown: 'Copy this link and send it to the owner.',
    opened: 'Pick their group in Telegram: the bot joins it, and the group is linked to this lead.',
    off: 'The Telegram bot is off.',
    failed: 'That did not work. Try again.',
  }[state];
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={btn.secondary} onClick={copy}>
          Copy bot link
        </button>
        <button type="button" className={btn.secondary} onClick={addToGroup}>
          Add the bot to their group
        </button>
      </div>
      <p className="mt-1.5 text-xs text-fg-muted">{note}</p>
      {state === 'shown' && (
        <input readOnly aria-label="Bot link" value={url} onFocus={(e) => e.target.select()} className={`mt-1.5 ${field.input}`} />
      )}
    </div>
  );
};
